import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search as SearchIcon, HelpCircle, Info } from "lucide-react";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";

const ChevronIcon: React.FC = () => (
    <svg className="h-5 w-5 text-gray-400 group-open:rotate-180 transition-transform" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
    </svg>
);

const HelpCenterPage: React.FC = () => {
    const [q, setQ] = useState("");

    const faqs = useMemo(() => ([
        { id: 1, cat: "Pedidos", q: "¿Cómo puedo cancelar mi pedido?", a: "Puedes cancelar desde Mis pedidos si aún no ha sido enviado." },
        { id: 2, cat: "Pagos", q: "¿Qué métodos de pago aceptan?", a: "Tarjeta débito/crédito, Yape, Culqui y contraentrega (según zona)." },
        { id: 3, cat: "Envíos", q: "¿Cuánto tarda el envío?", a: "Entre 24-72h en Lima y 3-7 días en provincias, según transportista." },
        { id: 4, cat: "Devoluciones", q: "¿Cómo tramito una devolución?", a: "Desde Mis pedidos > Detalle > Solicitar devolución dentro de 7 días." },
        { id: 5, cat: "Cuenta", q: "Olvidé mi contraseña", a: "Usa 'Olvidé mi contraseña' en el login y sigue las instrucciones." },
    ]), []);

    const filtered = faqs.filter((f) =>
        [f.q, f.a, f.cat].some((t) => t.toLowerCase().includes(q.toLowerCase()))
    );

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-6">
            <div className="mx-auto w-full max-w-5xl">
                <div className="mb-6 text-center">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Centro de ayuda</h1>
                    <p className="text-gray-600">Encuentra respuestas rápidas o contáctanos</p>
                </div>

                <div className="mb-6 max-w-2xl mx-auto">
                    <Input
                        value={q}
                        onChange={(e: any) => setQ(e.target.value)}
                        placeholder="Buscar en ayuda (pedidos, pagos, envíos, cuenta)…"
                        leftIcon={<SearchIcon className="w-5 h-5" />}
                        fullWidth
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-3">
                        {filtered.length === 0 ? (
                            <div className="rounded-xl border bg-white p-6 text-center text-gray-600">
                                No encontramos resultados para tu búsqueda.
                            </div>
                        ) : (
                            filtered.map((f) => (
                                <details key={f.id} className="group rounded-xl border bg-white p-4">
                                    <summary className="flex cursor-pointer list-none items-center justify-between">
                                        <div className="font-medium text-gray-900">{f.q}</div>
                                        <ChevronIcon />
                                    </summary>
                                    <div className="mt-2 text-gray-600">{f.a}</div>
                                    <div className="mt-3 text-xs text-gray-500">Categoría: {f.cat}</div>
                                </details>
                            ))
                        )}
                    </div>

                    <div className="space-y-3">
                        <div className="rounded-xl border bg-white p-5">
                            <div className="mb-2 flex items-center gap-2 text-gray-900 font-semibold">
                                <HelpCircle className="w-5 h-5 text-primary-600" /> Soporte por email
                            </div>
                            <p className="text-sm text-gray-600">
                                Escríbenos a <a href="mailto:soporte@eisgemi.com" className="text-primary-600">soporte@eisgemi.com</a>
                            </p>
                        </div>

                        <div className="rounded-xl border bg-white p-5">
                            <div className="mb-2 flex items-center gap-2 text-gray-900 font-semibold">
                                <Info className="w-5 h-5 text-primary-600" /> Políticas
                            </div>
                            <p className="text-sm text-gray-600">Consulta envíos, devoluciones y reembolsos en nuestras políticas.</p>
                            <div className="mt-3">
                                <Button
                                    //asChild
                                    variant="outline" size="sm">
                                    <Link to="/policies">Ver políticas</Link>
                                </Button>
                            </div>
                        </div>

                        <div className="rounded-xl border bg-white p-5">
                            <div className="flex items-center justify-between">
                                <span className="text-gray-900 font-semibold">Estado de servicios</span>
                                <Badge variant="success">Operativo</Badge>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Última actualización: {new Date().toLocaleString()}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HelpCenterPage;
